import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/ui/states';
import { Table, TableWrap, THead, TH, TR, TD } from '@/components/ui/table';
import {
  FISCAL_ROUTES,
  FISCAL_PERMISSIONS,
  FiscalRegime,
  apiMessage,
  type FiscalItemMapping,
  type FiscalTaxMapping,
  type UpsertFiscalItemMappingBody,
} from '../fiscalApi';

const EMPTY: UpsertFiscalItemMappingBody = {
  regime: FiscalRegime.EG_ETA,
  catalogItemId: '',
  codeSystem: 'EGS',
  itemCode: '',
  unitCode: '',
  taxMappingId: '',
};

interface CatalogItemOption {
  id: string;
  name: string;
  sku?: string | null;
}

/**
 * Each catalogue item's fiscal code (`docs/fiscal.md` step 5). The regime
 * validates the code's shape HERE — at configuration time — so a malformed
 * EGS code is a red field on this screen and not four hundred rejected
 * receipts on Friday night (ADR-0005 §5). An item picker needs `menu.view`,
 * which `fiscal.configure` does not imply; a holder without it still gets a
 * usable form via the plain item-id field this falls back to.
 */
export default function ItemMappingsSection() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const [target, setTarget] = useState<FiscalItemMapping | 'new' | null>(null);
  const [form, setForm] = useState<UpsertFiscalItemMappingBody>(EMPTY);
  const [itemSearch, setItemSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const mappings = useApi<unknown>(can(FISCAL_PERMISSIONS.view) ? FISCAL_ROUTES.itemMappings : null);
  const { rows } = rowsOf<FiscalItemMapping>(mappings.data);

  const taxMappings = useApi<unknown>(target && can(FISCAL_PERMISSIONS.view) ? FISCAL_ROUTES.taxMappings : null);
  const { rows: taxRows } = rowsOf<FiscalTaxMapping>(taxMappings.data);

  const canBrowseItems = can('menu.view');
  const catalogItems = useApi<unknown>(
    target && canBrowseItems ? '/catalog' : null,
    { search: itemSearch || undefined, limit: 25 },
  );
  const { rows: itemRows } = rowsOf<CatalogItemOption>(catalogItems.data);

  useEffect(() => {
    if (!target) return;
    if (target === 'new') setForm(EMPTY);
    else
      setForm({
        regime: target.regime,
        catalogItemId: target.catalogItemId,
        codeSystem: target.codeSystem,
        itemCode: target.itemCode,
        unitCode: target.unitCode ?? '',
        taxMappingId: target.taxMappingId ?? '',
      });
    setItemSearch('');
  }, [target]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.put(FISCAL_ROUTES.itemMappings, {
        ...form,
        unitCode: form.unitCode?.trim() || undefined,
        taxMappingId: form.taxMappingId?.trim() || undefined,
      });
      toast.success(t('fiscal.itemMappings.saved'));
      setTarget(null);
      mappings.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: FiscalItemMapping) => {
    const ok = await confirm({
      title: t('fiscal.itemMappings.removeTitle'),
      confirmLabel: t('common.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(FISCAL_ROUTES.itemMapping(row.id));
      toast.success(t('fiscal.itemMappings.removed'));
      mappings.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.saveFailed')));
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('fiscal.itemMappings.title')}</h2>
        {can(FISCAL_PERMISSIONS.configure) && (
          <Button size="sm" onClick={() => setTarget('new')}>
            <Icon as={Plus} />
            {t('fiscal.itemMappings.new')}
          </Button>
        )}
      </div>

      <TableWrap>
        {mappings.loading ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <div className="grid place-items-center gap-1 py-10 text-center">
            <p className="text-sm font-medium">{t('fiscal.itemMappings.empty')}</p>
            <p className="text-sm text-ink-muted">{t('fiscal.itemMappings.emptyHint')}</p>
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('fiscal.itemMappings.columns.item')}</TH>
                <TH>{t('fiscal.itemMappings.columns.codeSystem')}</TH>
                <TH>{t('fiscal.itemMappings.columns.itemCode')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.map((row) => (
                <TR key={row.id}>
                  <TD>{row.catalogItem?.name ?? row.catalogItemId}</TD>
                  <TD className="text-xs text-ink-muted">{row.codeSystem}</TD>
                  <TD className="font-mono text-xs">{row.itemCode}</TD>
                  <TD>
                    {can(FISCAL_PERMISSIONS.configure) && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setTarget(row)}>
                          <Icon as={Pencil} size="sm" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(row)}>
                          <Icon as={Trash2} size="sm" className="text-red-600 dark:text-red-400" />
                        </Button>
                      </div>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </TableWrap>

      {target && (
        <Dialog
          open
          onClose={() => setTarget(null)}
          title={target === 'new' ? t('fiscal.itemMappings.new') : t('fiscal.itemMappings.edit')}
          className="max-w-md"
          footer={
            <>
              <Button variant="outline" onClick={() => setTarget(null)} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <Button onClick={submit} disabled={busy || !form.catalogItemId.trim() || !form.itemCode.trim()}>
                {t('common.save')}
              </Button>
            </>
          }
        >
          <div className="space-y-3">
            {canBrowseItems ? (
              <Field label={t('fiscal.itemMappings.columns.item')} required controlId="item-map-search">
                <Input
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  placeholder={t('fiscal.itemMappings.searchPlaceholder')}
                  className="mb-2"
                />
                <Select
                  value={form.catalogItemId}
                  onChange={(e) => setForm((f) => ({ ...f, catalogItemId: e.target.value }))}
                >
                  <option value="">{t('fiscal.itemMappings.chooseItem')}</option>
                  {itemRows.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} {item.sku ? `(${item.sku})` : ''}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : (
              <Field label={t('fiscal.itemMappings.columns.item')} required hint={t('fiscal.itemMappings.noPickerHint')} controlId="item-map-id">
                <Input
                  value={form.catalogItemId}
                  onChange={(e) => setForm((f) => ({ ...f, catalogItemId: e.target.value }))}
                />
              </Field>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label={t('fiscal.itemMappings.columns.codeSystem')} required controlId="item-map-code-system">
                <Input value={form.codeSystem} onChange={(e) => setForm((f) => ({ ...f, codeSystem: e.target.value }))} />
              </Field>
              <Field label={t('fiscal.itemMappings.columns.itemCode')} required controlId="item-map-code">
                <Input value={form.itemCode} onChange={(e) => setForm((f) => ({ ...f, itemCode: e.target.value }))} />
              </Field>
            </div>

            <Field label={t('fiscal.itemMappings.unitCode')} controlId="item-map-unit">
              <Input value={form.unitCode ?? ''} onChange={(e) => setForm((f) => ({ ...f, unitCode: e.target.value }))} />
            </Field>

            <Field label={t('fiscal.itemMappings.taxMapping')} controlId="item-map-tax">
              <Select
                value={form.taxMappingId ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, taxMappingId: e.target.value }))}
              >
                <option value="">{t('fiscal.itemMappings.taxMappingDefault')}</option>
                {taxRows.map((tax) => (
                  <option key={tax.id} value={tax.id}>
                    {tax.label} ({tax.code})
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Dialog>
      )}
    </section>
  );
}
